'use strict';

const {
    makeAdClickedRequestAndRedirect,
    makeAdInViewportRequest
} = require('./adformEventRequest');

const {
    expandProductClickedTrackingUrl,
    expandProductInViewportTrackingUrl
} = require('./helpers/adformElementEventHelpers');
const adformConstants = require('./adformConstants');

/**
 * Detect when element is in viewport and inform adform with request.
 *
 * @param {Element} element - element we check
 * @param {string} impTrackerURL - url to inform adform
 */
function fireImpTrackerURLWhenInViewPort(element, impTrackerURL) {
    const observer = new IntersectionObserver((entries) => {
        if (entries[0]?.isIntersecting) {
            makeAdInViewportRequest(impTrackerURL);
            observer.unobserve(element);
        }
    }, {
        threshold: 1
    });

    if (!(element instanceof HTMLImageElement) || element.complete) {
        observer.observe(element);
        return;
    }

    element.addEventListener('load', () => {
        observer.observe(element);
    });
}

/**
 * Init observer to observe when element will be in viewport (to make adform request ad is viewed)
 * Note: can be used for elements that are not hidden
 * (e.g. mega menu ad couldn't be handled with this approach => it would fire even when mega menu is not opened)
 *
 * @param {Element} adformElement - adform element (link with img inside)
 * @param {Object} parsedBid - object holding info for this bid (url, price...)
 */
function initInViewportObserver(adformElement, parsedBid) {
    if (!adformElement
        || !parsedBid
        || !parsedBid.impTrackerURL
    ) {
        return;
    }

    const intersectionIndicatorElement = adformElement.querySelector(`.${adformConstants.BID_INTERSECTION_INDICATOR_JS_CLASS}`);
    if (!intersectionIndicatorElement) {
        return;
    }

    fireImpTrackerURLWhenInViewPort(intersectionIndicatorElement, parsedBid.impTrackerURL);
}

/**
 * Init observer to observe when product tile sponsored ad will be in viewport (to make adform request ad is viewed)
 *
 * @param {Element} productTile - productTile element
 * @param {Object} parsedBid - object holding info for this bid (url, price...)
 */
function initSponsoredProductAdInViewportObserver(productTile, parsedBid) {
    const updatedTrackerUrl = expandProductInViewportTrackingUrl(productTile, parsedBid);
    if (!updatedTrackerUrl) {
        return;
    }

    fireImpTrackerURLWhenInViewPort(productTile, updatedTrackerUrl);
}

/**
 * Init click event (to make adform request ad is clicked)
 *
 * @param {Element} adformElement - adform element (link with img inside)
 * @param {Object} parsedBid - object holding info for this bid (url, price...)
 */
function initClickEvent(adformElement, parsedBid) {
    if (!adformElement
        || !parsedBid
        || !parsedBid.vanityURL
        || !parsedBid.linkURL
    ) {
        return;
    }

    adformElement.addEventListener('click', function (event) {
        event.preventDefault();
        makeAdClickedRequestAndRedirect(parsedBid.linkURL, parsedBid.vanityURL);
    });
}

/**
 * Init event to fire request when product tile sponsored ad is clicked.
 * Then, open pdp page.
 *
 * @param {Element} productTile - productTile element
 * @param {Object} parsedBid - object holding info for this bid (url, price...)
 * @param {string} pdpURL - pdp url to redirect to
 */
function initSponsoredProductAdClickEvent(productTile, parsedBid, pdpURL) {
    const updatedTrackerUrl = expandProductClickedTrackingUrl(productTile, parsedBid);
    if (!productTile
        || !updatedTrackerUrl
        || !pdpURL
    ) {
        return;
    }

    productTile.querySelectorAll(`a[href="${pdpURL}"]`).forEach(pdpLink => {
        pdpLink.addEventListener('click', function (event) {
            event.preventDefault();
            makeAdClickedRequestAndRedirect(updatedTrackerUrl, pdpURL);
        });
    });
}

/**
 * Detects if mega menu ad img is in viewport (mega menu opened) and loaded.
 * If so, fires inViewport adform request.
 *
 * @param {Element} megaMenuAdImg - mega menu ad image
 */
function handleMegaMenuAdInViewport(megaMenuAdImg) {
    const viewportRequestFiredJsClass = 'js-in-viewport';
    if (
        megaMenuAdImg.classList.contains(viewportRequestFiredJsClass)
        || !megaMenuAdImg.complete
    ) {
        return;
    }

    const megaMenu = megaMenuAdImg.closest('.ddtmm-mega-menu');
    if (!megaMenu) {
        return;
    }

    const isMegaMenuVisible = window.getComputedStyle(megaMenu).visibility === 'visible';
    if (isMegaMenuVisible) {
        const impTrackerURL = megaMenuAdImg.getAttribute('data-url');
        if (!impTrackerURL) {
            return;
        }
        makeAdInViewportRequest(impTrackerURL);
        megaMenuAdImg.classList.add(viewportRequestFiredJsClass);
        megaMenuAdImg.removeAttribute('data-url');
    }
}

/**
 * Init event to fire request when featured block link is clicked (product, banner...)
 * Then, open link url.
 *
 * @param {Element} wrapper - featured block wrapper element
 * @param {string} bidLinkURL - url to fire request
 */
function initFeaturedBlockClickEvent(wrapper, bidLinkURL) {
    if (!wrapper
        || !bidLinkURL
    ) {
        return;
    }

    wrapper.querySelectorAll('a[href]').forEach(link => {
        link.addEventListener('click', (event) => {
            event.preventDefault();
            const redirectUrl = link.getAttribute('href');
            if (redirectUrl) {
                makeAdClickedRequestAndRedirect(bidLinkURL, redirectUrl);
            }
        });
    });
}

module.exports = {
    initInViewportObserver: initInViewportObserver,
    initClickEvent: initClickEvent,
    initSponsoredProductAdClickEvent: initSponsoredProductAdClickEvent,
    initSponsoredProductAdInViewportObserver: initSponsoredProductAdInViewportObserver,
    handleMegaMenuAdInViewport: handleMegaMenuAdInViewport,
    initFeaturedBlockClickEvent: initFeaturedBlockClickEvent
};
